import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/d7fi8np5vm7vckkoe54g';

const faqs = [
  {q:'How quickly can I get started?',a:'Most businesses onboard within 48 hours. Our team walks you through every step.'},
  {q:'Is there a minimum order volume?',a:'No minimum. We serve businesses from 50 orders/month to tens of thousands.'},
  {q:'Do you handle returns?',a:'Yes, we offer full returns management including inspection, repackaging, and restocking.'},
  {q:'What integrations are supported?',a:'Native support for Shopify, WooCommerce, Jumia, and Konga. Custom API for enterprise.'},
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number|null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [form, setForm] = useState({fn:'',ln:'',company:'',phone:'',email:'',industry:'',service:'',message:''});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      const body = new URLSearchParams({
        first_name: form.fn,
        last_name: form.ln,
        company: form.company,
        phone: form.phone,
        email: form.email,
        industry: form.industry,
        service: form.service,
        message: form.message,
      });
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
    } catch {
      setSubmitError('Something went wrong. Please try again or email us directly at info@thehubng.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-thl-dark">
      <Navbar />
      <div className="page-header pt-28 pb-16 px-6">
        <div className="page-header-grid" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-3 text-xs text-white/40">
            <button onClick={()=>navigate('/')} className="hover:text-white cursor-pointer">Home</button>
            <span>/</span><span className="text-white/60">Contact</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">Let&apos;s Build Your Logistics Together</h1>
          <p className="text-white/50 max-w-xl">Whether you need a quote, a facility tour, or a free consultation, our team is ready.</p>
        </div>
      </div>
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14">
          {/* Left info */}
          <div>
            <p className="eyebrow">Get in Touch</p>
            <h2 className="section-heading mb-4">We&apos;d Love to Hear From You</h2>
            <p className="text-white/50 text-sm mb-8">Our logistics advisors are available Mon–Fri: 8am–5pm &amp; Sat: 9am–3pm WAT.</p>
            {[
              {ico:'📍',t:'Headquarters',d:'15 Kudirat Abiola Way Oregun, Lagos'},
              {ico:'📞',t:'Phone',d:'07003000009'},
              {ico:'✉',t:'Email',d:'info@thehubng.com'},
              {ico:'🌐',t:'Website',d:'www.thehubng.com'},
              {ico:'🕐',t:'Hours',d:'Mon–Fri: 8am–5pm · Sat: 9am–3pm'}
            ].map(c=>(
              <div key={c.t} className="flex items-start gap-4 mb-5">
                <div className="w-9 h-9 flex items-center justify-center bg-thl-dark3 border border-thl-border rounded-lg text-base shrink-0">{c.ico}</div>
                <div><h4 className="text-white font-semibold text-sm">{c.t}</h4><p className="text-white/50 text-sm">{c.d}</p></div>
              </div>
            ))}
            <h3 className="text-white font-bold text-base mt-8 mb-4">Common Questions</h3>
            <div className="flex flex-col gap-2">
              {faqs.map((faq,i)=>(
                <div key={i} className="bg-thl-dark3 border border-thl-border rounded-xl overflow-hidden">
                  <button onClick={()=>setOpenFaq(openFaq===i?null:i)} className="w-full flex items-center justify-between px-5 py-4 text-left text-sm text-white font-medium cursor-pointer hover:bg-white/5 transition-colors">
                    {faq.q}<span className="text-thl-red font-bold text-lg">{openFaq===i?'−':'+'}</span>
                  </button>
                  <div className={`faq-answer px-5 text-white/50 text-sm ${openFaq===i?'open pb-4':''}`}>{faq.a}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Right form */}
          <div className="bg-thl-dark3 border border-thl-border rounded-2xl p-8">
            <h3 className="text-white font-bold text-xl mb-1">Request a Quote or Tour</h3>
            <p className="text-white/40 text-sm mb-6">We&apos;ll get back to you within 2 business hours.</p>
            {submitted ? (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">✓</div>
                <p className="text-white font-bold text-lg mb-2">Request Received!</p>
                <p className="text-white/50 text-sm">We&apos;ll contact you within 2 business hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-white/50 text-xs mb-1 block">First Name *</label><input required value={form.fn} onChange={e=>setForm({...form,fn:e.target.value})} placeholder="Adaeze" className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50" /></div>
                  <div><label className="text-white/50 text-xs mb-1 block">Last Name *</label><input required value={form.ln} onChange={e=>setForm({...form,ln:e.target.value})} placeholder="Okonkwo" className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50" /></div>
                </div>
                <div><label className="text-white/50 text-xs mb-1 block">Company *</label><input required value={form.company} onChange={e=>setForm({...form,company:e.target.value})} placeholder="BrightSkin Nigeria Ltd" className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-white/50 text-xs mb-1 block">Phone *</label><input required type="tel" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+234 800 000 0000" className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50" /></div>
                  <div><label className="text-white/50 text-xs mb-1 block">Email *</label><input required type="email" name="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="you@brand.ng" className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50" /></div>
                </div>
                <div><label className="text-white/50 text-xs mb-1 block">Industry *</label>
                  <select required value={form.industry} onChange={e=>setForm({...form,industry:e.target.value})} className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-thl-red/50">
                    <option value="">Select industry</option>
                    {['Ecommerce / DTC','Retail','FMCG','Pharmaceuticals','Fashion & Apparel','Electronics','Banks / Fintech','Renewable Energy','NGO / Humanitarian','Importer / Distributor','SME / Other'].map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
                <div><label className="text-white/50 text-xs mb-1 block">Service Interested In *</label>
                  <select required value={form.service} onChange={e=>setForm({...form,service:e.target.value})} className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-thl-red/50">
                    <option value="">Select a service</option>
                    {['Smart Fulfillment','Flexible Storage','Inventory Management','Last-Mile Delivery Coordination','Ecommerce Fulfillment','Supply Chain Consulting','Facility Tour','Full Logistics Solution'].map(o=><option key={o}>{o}</option>)}
                  </select>
                </div>
                <div><label className="text-white/50 text-xs mb-1 block">Message</label><textarea value={form.message} onChange={e=>setForm({...form,message:e.target.value})} maxLength={500} rows={3} placeholder="Tell us about your business..." className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50 resize-none" /></div>
                {submitError && (
                  <p className="text-red-400 text-xs text-center">{submitError}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending…' : 'Submit Request →'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <p className="eyebrow">Find Us</p>
            <h2 className="section-heading">Our Location</h2>
            <p className="text-white/50 text-sm mt-2">15 Kudirat Abiola Way, Oregun, Lagos</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-thl-border w-full" style={{ height: 420 }}>
            <iframe
              title="THL Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3963.3519!2d3.3553!3d6.5966!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103b9228a8975e35%3A0x3f0b3e0e0a0e0e0e!2s15%20Kudirat%20Abiola%20Way%2C%20Oregun%2C%20Lagos!5e0!3m2!1sen!2sng!4v1700000000000!5m2!1sen!2sng"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
